import flatpickr from "flatpickr";
import { German } from "flatpickr/dist/l10n/de.js";
import { apiService } from '../custom/tariff_api.es6';

// Selectors
const SELECTORS = {
    ALL_INPUTS: '.ninja-forms-field:not(.select2-hidden-accessible)',
    FIELD_CONTAINER: '.nf-field-container',
    FIELD_LABELS: '.field-wrap label',
    ENABLE_SELECT2: '.enable-select2 select',
    NINJA_FORMS_FIELD: '.ninja-forms-field',
    DATE_PICKER: '.date-container .datepicker, .date-container .datepicker-18, .date-container .datepicker-5',
    DATE_ICON: '.date-icon',
    DATE_INPUT: '.date-container .input',
    FORM: '#revision-form',
    DATE_LABEL: '.date-container label',
    STREET: 'select[name=street]',
    POSTAL_CODE: 'select[name=postalcode_ort]',
    CITY: '[name="city"]',
    OLD_SUPPLIER_NAME: '[name="oldSupplier"]',
    FORM_CONTAINER: '.form-box',
    LOADER: '#loader',
    RESPONSE_ERRORS: '.response-errors',
    SUBMIT_BUTTON: '[type="submit"]',
    NAME_FIELDS: '[name="firstname"], [name="lastname"]',
};

const fieldMapping = {
    'businessPartner.firstName': 'firstname',
    'businessPartner.name': 'lastname',
    'supplyAddress.street': 'street',
    'supplyAddress.housenumber': 'housenumber',
    'supplyAddress.housenumberAddition': 'housenumberAddition',
    'supplierOldILN': 'oldSupplier',
    'meterNumber': 'deliveryPointMeteringDeviceNumber',
    'meteringPoint': 'marketLocation',
    'supplierOldCustomerId': 'oldSupplierCustomerNumber'
};

const allowedFields = {
    businessPartner: ['businessPartnerNumber', 'firstName', 'name', 'dateOfBirth'],
    supplyAddress: ['street', 'housenumber', 'housenumberAddition', 'postalCode', 'city'],
    meterNumber: true,
    meteringPoint: true,
    supplierOldILN: true,
    supplierOldCustomerId: true,
};

class RevisionApiService {
    constructor() {
        this._loading = false; // Internal loading state
        this.onLoadingChange = null; // Callback for loading state changes
        this.authorizationHeader = this.generateAuthorizationHeader();
        // Absolute path (from myajax, localized in functions.php via get_stylesheet_directory_uri())
        // rather than a relative "../" path - a relative path breaks once the current URL is
        // nested more than one segment deep, since "../" only pops a single path segment
        // instead of resolving back to the site root.
        this.proxyUrl = (typeof myajax !== 'undefined' && myajax.proxy_url) || '../wp-content/themes/gruuun-theme/api-proxi.php';
        this.debounceDelay = 300;
        this.loading = false;
        this.correctionData = {};
        this.nameCanged = false;
        this.tokenValue = '';

        this.getTokenValue();

        this.initializeEventListeners();

        this.renderForm();
    }

    // Getter for loading state
    get loading() {
        return this._loading;
    }

    // Setter for loading state with additional logic
    set loading(value) {
        if (this._loading !== value) {
            this._loading = value;
            // Trigger callback if defined
            if (typeof this.onLoadingChange === "function") {
                this.onLoadingChange(value);
            }
        }
    }

    getTokenValue() {
        let searchParams = new URLSearchParams(window.location.search);
        if (searchParams.has('token')) {
            this.tokenValue = searchParams.get('token');
        }
    }

    // Initialize form logic
    initializeForm() {
        this.initializeSelect2($(SELECTORS.ENABLE_SELECT2), { minimumResultsForSearch: -1 });
        this.initializeSupplierSelect2(SELECTORS.OLD_SUPPLIER_NAME);
        this.initializePostalCodeSelect2(SELECTORS.POSTAL_CODE);
        apiService.initializeSelect2(SELECTORS.STREET, { multiple: true, minimumResultsForSearch: 999, maximumSelectionLength: 1 });
        this.initializeDatePickers();
    }

    validateForm() {
        this.setupValidationMessages();
        this.setupValidationMethods();
        this.setupFormValidation();
        this.checkFields();
        this.checkLabels();
    }

    initializeEventListeners() {
        // Global event listeners here

        $(document).on('click', SELECTORS.DATE_LABEL, (e) => this.handleDateLabelClick(e));

        $(document).on('keyup blur change', SELECTORS.ALL_INPUTS, (e) => this.handleInputValidation(e));

        // Handle input events
        $(document).on('input', SELECTORS.NINJA_FORMS_FIELD, (e) => this.handleInputEvent(e));

        // Handle focus events
        $(document).on('focus', SELECTORS.NINJA_FORMS_FIELD, (e) => this.handleFocusEvent(e));

        // Handle blur events
        $(document).on('blur', SELECTORS.NINJA_FORMS_FIELD, (e) => this.handleBlurEvent(e));

        // Handle date input events
        $(document).on('focus', SELECTORS.DATE_INPUT, (e) => this.handleDateInputFocus(e));
        $(document).on('blur', SELECTORS.DATE_INPUT, (e) => this.handleDateInputBlur(e));

        // Name fields change
        $(document).on('change', SELECTORS.NAME_FIELDS, (e) => {
            if(!this.nameCanged) {
                this.nameCanged = true;
            }
        });

        $(document).on('submit', SELECTORS.FORM, (e) => this.handleFormSubmit(e));

        this.onLoadingChange = (isLoading) => {
            if (isLoading) {
                $(SELECTORS.SUBMIT_BUTTON).prop('disabled', true).addClass('-loading');
            } else {
                $(SELECTORS.SUBMIT_BUTTON).prop('disabled', false).removeClass('-loading');
            }
        };
    }

    generateAuthorizationHeader() {
        const encodedCredentials = btoa(`${this.username}:${this.password}`);
        return `Basic ${encodedCredentials}`;
    }

    showLoader() {
        $(SELECTORS.LOADER).show();
    }

    hideLoader() {
        $(SELECTORS.LOADER).hide();
    }

    setupValidationMessages() {
        $.extend($.validator.messages, {
            required: "Dieses Feld wird benötigt.",
            email: "Bitte geben Sie eine gültige E-Mail-Adresse ein.",
            number: "Bitte geben Sie eine gültige Zahl ein.",
            range: $.validator.format("Bitte geben Sie einen Wert zwischen {0} und {1} ein."),
            max: $.validator.format("Bitte geben Sie einen Wert kleiner oder gleich {0} ein."),
            min: $.validator.format("Bitte geben Sie einen Wert größer oder gleich {0} ein."),
            digits: "Nur Zahlen sind erlaubt.",
            rangelength: $.validator.format("Bitte geben Sie genau {0} Zeichen ein."),
        });
    }

    setupValidationMethods() {
        $.validator.addMethod("customEmail", function (value, element) {
            return this.optional(element) || /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);
        }, "Bitte geben Sie eine gültige E-Mail-Adresse ein.");

        $.validator.addMethod("alphanumeric", function (value, element) {
            return this.optional(element) || /^[a-zA-Z0-9]+$/.test(value);
        }, "Bitte geben Sie nur Buchstaben und Zahlen ein.");

        $.validator.addMethod("lettersDigitsHyphen", function (value, element) {
            return this.optional(element) || /^[a-zA-Z0-9-]+$/.test(value);
        }, "Bitte geben Sie nur Buchstaben, Zahlen und Bindestriche ein.");

        $.validator.addMethod("numeric_period_hyphen", function (value, element) {
            return this.optional(element) || /^[A-Za-z0-9.-]*$/.test(value);
        }, "Bitte geben Sie nur Zahlen, Punkte und Bindestriche ein.");

        $.validator.addMethod("lettersWithDashesDiacritics", function (value, element) {
            return this.optional(element) || /^[a-zA-ZÀ-ž\- ]+$/.test(value);
        }, "Bitte geben Sie nur Buchstaben, Bindestriche und diakritische Zeichen ein.");

        $.validator.addMethod("dateRange", function (value, element, params) {
            const selectedDate = new Date(value);
            const minDate = params[0];
            const maxDate = params[1];
            return this.optional(element) || (selectedDate >= minDate && selectedDate <= maxDate);
        }, "Ihr Alter muss über 18 oder unter 130 Jahre liegen.");

        $.validator.addMethod("numbersAndPlus", function (value, element) {
            return this.optional(element) || /^[0-9+]+$/.test(value);
        }, "Bitte geben Sie nur Zahlen und '+' ein.");

        $.validator.addMethod("lettersAndDash", function (value, element) {
            return this.optional(element) || /^[a-zA-ZäöüßÄÖÜ\s-]+$/.test(value);
        }, "Bitte geben Sie nur Buchstaben und '-' ein.");

        $.validator.addMethod("lettersAndDashAndDot", function (value, element) {
            return this.optional(element) || /^[a-zA-ZäöüßÄÖÜ\s\-.]+$/.test(value);
        }, "Bitte geben Sie nur Buchstaben, '-', '.' und Leerzeichen ein.");

        $.validator.addMethod("lettersNumbersSlash", function (value, element) {
            return this.optional(element) || /^[a-zA-ZäöüßÄÖÜ0-9\s/]+$/.test(value);
        }, "Bitte geben Sie nur Buchstaben, Zahlen und '/' ein.");

        $.validator.addMethod("notOlderThan5Weeks", function (value, element) {
            const selectedDate = new Date(value);
            const currentDate = new Date();
            const fiveWeeksAgo = new Date();
            fiveWeeksAgo.setDate(currentDate.getDate() - 35);
            return this.optional(element) || selectedDate >= fiveWeeksAgo;
        }, "Dein Einzugsdatum liegt mehr als 4 Wochen zurück.");
    }

    setupFormValidation(form) {
        const maxDate = new Date();
        maxDate.setFullYear(maxDate.getFullYear() - 18);
        maxDate.setHours(0, 0, 0, 0);

        const minDate = new Date();
        minDate.setFullYear(minDate.getFullYear() - 130);
        minDate.setHours(0, 0, 0, 0);

        $(SELECTORS.FORM).validate({
            ignore: ".datepicker-18.input, .datepicker-5.input, .datepicker.input",
            rules: {
                email: { customEmail: true },
                demail: { customEmail: true },
                deliveryPointMeteringDeviceNumber: { lettersDigitsHyphen: true },
                postalCode: { digits: true },
                firstname: { lettersWithDashesDiacritics: true },
                lastname: { lettersWithDashesDiacritics: true },
                housenumber: { alphanumeric: true },
                dateOfBirth: { dateRange: [minDate, maxDate] },
                supplyBeginDate: { notOlderThan5Weeks: true },
                phoneNumber: { numbersAndPlus: true },
                oldSupplierCustomerNumber: { numeric_period_hyphen: true },
            },
            errorPlacement: function (error, element) {
                error.appendTo(element.closest('.field-wrap').find('.nf-error-msg'));
            },
            highlight: function (element) {
                $(element).closest('.field-wrap').addClass('nf-error').removeClass('nf-pass');
            },
            unhighlight: function (element) {
                $(element).closest('.field-wrap').removeClass('nf-error');
            },
            success: function (label, element) {
                $(element).closest('.field-wrap').addClass('nf-pass');
            },
        });
    }


    initializeDatePickers() {
        this.setupFlatpickr(SELECTORS.DATE_PICKER);
    }

    setupFlatpickr(selector, additionalOptions = {}) {
        setTimeout(() => {
            $(selector).each(function () {
                const $pickerElement = $(this);

                const flatpickrInstance = $pickerElement.flatpickr({
                    locale: German,
                    altInput: true,
                    altFormat: "d.m.Y",
                    dateFormat: "Y-m-d",
                    allowInput: true,
                    clickOpens: false,
                    disableMobile: true,
                    additionalOptions,
                    onValueUpdate: function(selectedDates, dateStr, instance) {
                        $pickerElement.valid();
                    },
                    onClose: function(selectedDates, dateStr, instance){
                        $pickerElement.valid();
                    }
                });

                $pickerElement.closest(SELECTORS.FIELD_CONTAINER).find(SELECTORS.DATE_ICON).on("click", function () {
                    flatpickrInstance.open();
                });
            });
        }, 10);
    }

    handleDateInputFocus(event) {
        const $input = $(event.currentTarget);
        $input.inputmask({
            mask: "99.99.9999",
            placeholder: "tt.mm.jjjj",
            clearIncomplete: true,
        });
    }

    handleDateInputBlur(event) {
        const $input = $(event.currentTarget);
        $input.inputmask('remove');
        $input.attr('placeholder', '');
    }

    handleDateLabelClick(event) {
        event.preventDefault();

        const $label = $(event.currentTarget);
        const fieldId = $label.attr('for');
        let $currentField = $(`#${fieldId}`);

        if ($currentField.length > 0 && $currentField.attr('type') === 'hidden') {
            $currentField = $currentField.next();
        }

        $currentField.focus();
    }

    handleInputValidation(event) {
        let $input = $(event.currentTarget);
        const $attr = $input.attr('required');
        const $container = $input.closest(SELECTORS.FIELD_CONTAINER);
        const $wrap = $input.closest('.field-wrap');

        if ($input.length > 0) {
            if ($input.attr('type') === 'hidden') {
                $input = $input.next();
            }
        }

        // Validate the input
        $(SELECTORS.FORM).validate().element($input[0]);

        // Add or remove "fake-nf-pass" based on the input value
        if (($input.is('[type="text"]') || $input.is('[type="number"]') || $input.is('[type="tel"]') || $input.is('[type="date"]')) && !$attr) {
            if ($input.val().trim() !== '') {
                $container.addClass('fake-nf-pass');
            } else {
                $container.removeClass('fake-nf-pass');
                $wrap.removeClass('nf-pass');
            }
        }
    }

    handleInputEvent(event) {
        const $input = $(event.currentTarget);
        const $attr = $input.attr('required');
        const $container = $input.closest(SELECTORS.FIELD_CONTAINER);
        let value = $input.val();

        if ($input.is('select[multiple]')) {
            value = Array.isArray(value) && value.length > 0 ? value.join('').trim() : '';
        } else if ($input.is('select')) {
            value = value ? value.trim() : '';
        } else if (typeof value === 'string') {
            value = value.trim();
        } else {
            value = '';
        }

        $container.removeClass('potentially-required-label');
        if (value !== '') {
            $container.addClass('label-up');
            if (typeof $attr !== 'undefined' && $attr !== false) {
                $container.removeClass('required-label');
            }
        } else {
            $container.removeClass('label-up');
            if (typeof $attr !== 'undefined' && $attr !== false) {
                $container.addClass('required-label');
            }
        }
    }

    handleFocusEvent(event) {
        const $input = $(event.currentTarget);
        const $container = $input.closest(SELECTORS.FIELD_CONTAINER);

        $container.addClass('label-focus');
        $container.removeClass('potentially-required-label');
    }

    handleBlurEvent(event) {
        const $input = $(event.currentTarget);
        const $attr = $input.attr('required');
        const $container = $input.closest(SELECTORS.FIELD_CONTAINER);

        $container.removeClass('potentially-required-label');
        $container.removeClass('label-focus');
        if ($input.val().trim() === '') {
            if (typeof $attr !== 'undefined' && $attr !== false) {
                $container.addClass('required-label');
            }
        } else {
            if (typeof $attr !== 'undefined' && $attr !== false) {
                $container.removeClass('required-label');
            }
        }
    }

    // Check Ninja Forms fields
    checkFields() {
        $(SELECTORS.ALL_INPUTS).each(function () {
            let $input = $(this);
            let $attr = $input.attr('required');
            let $container = $input.closest(SELECTORS.FIELD_CONTAINER);

            if ($input.length > 0) {
                if ($input.attr('type') === 'hidden') {
                    $input = $input.next();
                }
            }

            $container.addClass('active-label');
            if (!$input.is('select') && !$input.is('input[type=checkbox]') && !$input.is('input[type=radio]')) {
                if ($input.val().trim() !== '') {
                    $container.addClass('label-up');
                } else {
                    $container.removeClass('label-up');
                }
            }
            if (typeof $attr !== 'undefined' && $attr !== false && ((!$.isArray($input.val()) && $input.val().trim() === '') || ($.isArray($input.val()) && $input.val().length > 0))) {
                $container.addClass('potentially-required-label');
            }

            if (($input.is('[type="text"]') || $input.is('[type="email"]') || $input.is('[type="tel"]') || $input.is('[type="number"]') || $input.is('[type="date"]')) && $input.val().trim() !== '') {
                $(SELECTORS.FORM).validate().element($input[0]);
            }

            if (($input.is('[type="text"]') || $input.is('[type="number"]') || $input.is('[type="tel"]') || $input.is('[type="date"]')) && !$attr && $input.val().trim() !== '') {
                $container.addClass('fake-nf-pass');
            }
        });
    }

    // Update label styling
    checkLabels() {
        $(SELECTORS.FIELD_LABELS).each(function () {
            let labelText = $(this).html();
            if (labelText.includes('(optional)')) {
                let updatedText = labelText.replace('(optional)', '<i>(optional)</i>');
                $(this).html(updatedText);
            }
        });
    }

    // Initialize Select2 for specific fields
    initializeSelect2(select, customOptions = {}) {
        const $select = $(select);
        const $this = this;

        $select.each(function () {
            const selectThis = $(this);
            $this.initializeSingleSelect2(selectThis, customOptions, $this).then(() => {
                $this.setupMultipleSelectHandlers(selectThis, customOptions);
            });
        });
    }

    initializeSingleSelect2($select, customOptions, apiService) {
        return new Promise((resolve) => {
            const defaultOptions = {
                escapeMarkup: function (markup) {
                    return markup;
                }
            };

            if (customOptions.multiple) {
                defaultOptions['dropdownParent'] = $select.parent();
            }

            const select2Options = $.extend(true, {}, defaultOptions, customOptions);

            if (!$select.hasClass('select2-hidden-accessible')) {
                $select.select2(select2Options);
                resolve();
            } else {
                $select.select2('destroy').select2(select2Options);
                resolve();
            }
        });
    }

    initializeSupplierSelect2(select) {
        // Define custom options for supplier select
        const customOptions = {
            ajax: {
                url: myajax.url,
                method: 'POST',
                dataType: 'json',
                delay: this.debounceDelay,
                data: function (params) {
                    return {
                        action: 'get_suppliers',
                        nonce: myajax.nonces.get_suppliers,
                        query: params.term // The search term entered by the user
                    };
                },
                processResults: function (data) {
                    return {
                        results: data.data // Map the data to the format expected by Select2
                    };
                },
                cache: true
            },
            language: {
                noResults: function () {
                    return 'keine Ergebnisse gefunden';
                },
                searching: function () {
                    return 'Suchen...';
                },
                inputTooShort: function (args) {
                    const remainingChars = args.minimum - args.input.length;
                    return `Bitte geben Sie noch ${remainingChars} oder mehr Zeichen ein`;
                }
            },
            maximumSelectionLength: 1,
            placeholder: 'Bisheriger Energieversorger',
            allowClear: true,
            multiple: true,
            minimumResultsForSearch: 999,
            minimumInputLength: 3 // Only start searching when the user has input 3 or more characters
        };

        // Initialize select2 with custom options for suppliers
        this.initializeSelect2(select, customOptions);
    }

    updateStreets(postalCode, city, $select, defaultValue = null) {
        $select.addClass('-load');
        $select.empty().trigger('change');
        const loadingOption = new Option('Wird geladen...', 1, true, true);
        $select.append(loadingOption).val(1).trigger('change');

        apiService.getStreetsByPostalCode(postalCode, city).then(streets => {
            $select.empty().trigger('change');

            const streetData = streets.streets.map(street => ({ id: street, text: street }));

            streetData.forEach(street => {
                const newOption = new Option(street.text, street.id, false, false);
                $select.append(newOption);
            });

            if (defaultValue) {
                $select.val(defaultValue).trigger('change');
            } else {
                $select.val(null).trigger('change');
            }

            setTimeout(() => {
                $select.removeClass('-load');
            }, 1);
        }).catch(error => {
            console.error('Failed to fetch streets:', error);
            $select.empty().trigger('change');
            const errorOption = new Option('Error loading streets', '', false, false);
            $select.append(errorOption).trigger('change');

            setTimeout(() => {
                $select.removeClass('-load');
            }, 1);
        });

    }

    initializePostalCodeSelect2(select) {
        // Define custom options for supplier select
        let $select = $(select);
        let $this = this;

        $select.select2({
            dropdownParent: $select.parent(),
            multiple: true,
            maximumSelectionLength: 1,
            minimumInputLength: 3,
            language: {
                inputTooShort: function () {
                    return 'Bitte gib mindestens 3 Zeichen ein'; // message for too few characters
                },
                noResults: function () {
                    return 'Keine Übereinstimmungen gefunden'; // no results found message
                }
            },
            ajax: {
                transport: function (params, success, failure) {
                    if (typeof postalCodes === 'undefined' || !Array.isArray(postalCodes) || postalCodes.length === 0) {
                        $select.empty();
                        failure();
                        return;
                    }

                    let searchTerm = params.data.term.toLowerCase();

                    // If less than 3 characters, clear the select and do not perform the search
                    if (searchTerm.length < 3) {
                        $select.empty();
                        return;
                    }

                    let isNumeric = /^\d+$/.test(searchTerm); // Check if the search term is a number

                    // Filter the postalCodes array based on the search term
                    let filteredData = postalCodes.filter(function (item) {
                        let combinedText = `${item.code} ${item.city}`.toLowerCase();

                        if (isNumeric) {
                            // If the search term is a number, check if it matches the start of the postal code
                            return item.code.startsWith(searchTerm);
                        } else {
                            // Otherwise, check if the combined text contains the search term
                            return combinedText.includes(searchTerm);
                        }
                    });

                    // Sort the filtered data by postal code in ascending order
                    filteredData.sort(function (a, b) {
                        return a.code.localeCompare(b.code, undefined, { numeric: true });
                    });

                    // If no matching results found, display a message in the console
                    if (filteredData.length === 0) {
                        console.warn('No matching postal codes or cities found for the search term: ' + searchTerm);
                    }

                    // Map filtered data to the format required by select2
                    let results = filteredData.map(function (item) {
                        return {
                            id: JSON.stringify({ code: item.code, city: item.city }), // JSON string for value
                            text: item.code + ' ' + item.city // Display postal code and city
                        };
                    });

                    // Return the filtered and sorted results
                    success({
                        results: results
                    });
                }
            }
        });

        $select.on('select2:open', function() {
            if($select.val().length) {
                let $selectContainer = $(this).next('.select2-container');
                let $searchField = $selectContainer.find('.select2-search__field');

                // Retrieve the text of the first selected item
                let selectedItem = $select.select2('data')[0];
                let selectedText = selectedItem ? $this.decodeHtmlEntities(selectedItem.text) : '';

                // Clear the selected item from Select2
                $select.val(null).trigger('change');
                $searchField.val(selectedText);
                $select.parent().addClass('-loading');

                // Insert the text into the search field for editing
                setTimeout(function () {
                    $searchField.trigger("input");
                    setTimeout(function () {
                        $select.parent().removeClass('-loading');
                    }, 1);
                }, 1);
            }
        });

        $select.on('select2:select', function(e) {
            const selectedData = e.params.data;

            try {
                const valueObject = JSON.parse(selectedData.id);

                if (valueObject.code && valueObject.city) {
                    let code = valueObject.code;
                    let city = valueObject.city;

                    $this.updateStreets(code, city, $(SELECTORS.STREET));
                }
            } catch (err) {
                console.error(err);
            }
        });

        $select.on('select2:close', function() {
            // Check if there is a selected item
            if ($select.val().length === 0) {
                let $selectResults = $select.data('select2').results.$results;

                // Find the first item in the results
                let results = $selectResults.find('.select2-results__option');

                if (results.length > 0) {
                    let firstResult = $(results[0]);

                    // Check if the first result is not a message indicating no results found
                    if (!firstResult.hasClass('select2-results__message')) {
                        // Retrieve the text of the first result
                        let firstText = firstResult.text();

                        // Split the text into code and city based on the first space
                        let code = firstText.split(" ", 1)[0]; // First word is the code
                        let city = firstText.slice(code.length + 1); // Remaining text is the city

                        // Create an ID in JSON format, similar to the format used in the ajax function
                        let id = JSON.stringify({ code: code, city: city });

                        // Remove all existing options to avoid duplicates
                        $select.find('option').remove();

                        // Create a new option based on the code and city
                        let option = new Option(firstText, id, true, true);
                        $select.append(option).trigger('change');

                        // Trigger the `select2:select` event to simulate selection
                        $select.trigger({
                            type: 'select2:select',
                            params: {
                                data: { id: id, text: firstText }
                            }
                        });
                    }
                }
            }
        });
    }

    fetchAndSelectSupplierByILN(selector, supplierILN) {
        if (!supplierILN) return;

        $.ajax({
            url: myajax.url,
            method: 'POST',
            dataType: 'json',
            data: {
                action: 'get_suppliers',
                nonce: myajax.nonces.get_suppliers,
                iln: supplierILN
            },
            success: function (response) {
                if (response && response.data && response.data.length > 0) {
                    // Expecting response.data to have [{ id: iln, text: supplierName }]
                    const supplier = response.data[0];
                    const preselectedOption = new Option(supplier.text, supplier.id, true, true);
                    $(selector).append(preselectedOption).trigger('change'); // Append and select the option
                }
            },
            error: function () {
                console.error('Failed to fetch supplier by ILN.');
            }
        });
    };

    setupMultipleSelectHandlers($select, customOptions) {
        let $this = this;
        if (customOptions.multiple) {
            const idAttr = $select.attr('id');
            const searchField = $select.parent().find('.select2-search__field');
            const searchContainer = $select.parent().find('.select2-selection--multiple');

            searchField.attr('autocomplete', 'autocomplete_off_' + idAttr);
            searchField.attr('aria-autocomplete', 'autocomplete_off_' + idAttr);

            searchField.prop('readonly', true);

            searchContainer.on('touchstart click', function () {
                searchField.prop('readonly', false);
            });

            $select.on('select2:opening', function () {
                searchField.prop('readonly', false);
            });

            $select.on('select2:open', function () {
                if ($select.val().length) {
                    let selectedItem = $select.select2('data')[0];
                    let selectedText = selectedItem ? $this.decodeHtmlEntities(selectedItem.text) : '';

                    if ($select.attr('name') === 'oldSupplier') {
                        $select.find('option').remove();
                    }
                    $select.val(null).trigger('change');

                    searchField.val(selectedText);
                    $select.parent().addClass('-loading');

                    setTimeout(() => {
                        searchField.trigger("input");
                        setTimeout(() => {
                            $select.parent().removeClass('-loading');
                        }, 1);
                    }, 1);
                }
            });

            $select.on('select2:select', function () {
                searchField.blur();
            });

            $select.on('select2:closing', function () {
                searchField.prop('readonly', true);
            });
        }
    }

    decodeHtmlEntities(str) {
        let txt = document.createElement("textarea");
        txt.innerHTML = str;
        return txt.value;
    }

    getCorrectionData() {
        if (this.loading === true) {
            return;
        }
        this.loading = true;
        this.showLoader();

        let sendData = {};

        if (this.tokenValue) {
            sendData.value = this.tokenValue;

            return new Promise((resolve, reject) => {
                $.ajax({
                    url: `${this.proxyUrl}?type=getCorrectionDataByCancellation`,
                    method: 'POST',
                    contentType: 'application/json',
                    data: JSON.stringify(sendData),
                    headers: {
                        'Authorization': this.authorizationHeader
                    },
                    success: function (response) {
                        resolve(response);
                    },
                    error: function (xhr, status, error) {
                        reject(error);
                    }
                });
            });
        } else {
            return Promise.reject('Token is missing in URL parameters');
        }
    }

    convertTimestampToISO(timestamp) {
        if (!timestamp) return null;
        const date = new Date(parseInt(timestamp, 10));
        return date.toISOString();
    }

    setNestedValue(obj, path, value) {
        const keys = path.split('.');
        keys.reduce((acc, key, index) => {
            if (index === keys.length - 1) {
                acc[key] = value;
            } else {
                acc[key] = acc[key] || {};
            }
            return acc[key];
        }, obj);
    }

    getEmptyForm() {
        const bodyClass = $('body').attr('class');
        const pageIdMatch = bodyClass.match(/page-id-(\d+)/);
        const pageId = pageIdMatch ? pageIdMatch[1] : null;

        return new Promise((resolve, reject) => {
            $.ajax({
                url: myajax.url,
                method: 'POST',
                data: {
                    action: 'get_correction_form',
                    page_id: pageId,
                    nonce: myajax.nonces.get_correction_form,
                },
                success: function (response) {
                    if (response.data && response.data.form) {
                        $(SELECTORS.FORM_CONTAINER).html(response.data.form);
                        console.log(response.data.template);
                        resolve();
                    } else {
                        reject('Empty response or form data is missing.');
                    }
                },
                error: function (xhr, status, error) {
                    reject(error);
                }
            });
        });
    }

    setCorrectionData(correctionData) {
        const $this = this;
        return new Promise((resolve, reject) => {
            try {
                const getNestedValue = (obj, path) => {
                    return path.split('.').reduce((value, key) => (value && value[key] !== undefined ? value[key] : null), obj);
                };

                const postalCode = getNestedValue(correctionData, 'supplyAddress.postalCode') || '';
                const city  = getNestedValue(correctionData, 'supplyAddress.city') || '';

                const fillTextField = (selector, value) => {
                    if ($(selector).length) {
                        $(selector).val(value).trigger('input');
                    }
                };

                const fillSelectField = (selector, value) => {
                    if (value) {
                        this.fetchAndSelectSupplierByILN(selector, value);
                    }
                };

                const fillSelectStreet = (selector, value) => {
                    if (value) {
                        $this.updateStreets(postalCode, city, $(selector), value)
                    }
                };

                const fillSelectZip = (selector, value, text) => {
                    if (value) {
                        const newOption = new Option(text, value, true, true);
                        $(selector).append(newOption).trigger('change');
                    }
                };

                const fillDateField = (selector, value) => {
                    if ($(selector).length) {
                        const date = new Date(value);
                        $(selector).flatpickr().setDate(date);
                    }
                };

                Object.entries(fieldMapping).forEach(([key, selector]) => {
                    const value = getNestedValue(correctionData, key);

                    if (value !== null) {
                        if (selector === 'dateOfBirth') {
                            fillDateField(`[name="${selector}"]`, value);
                        } else if (selector === 'oldSupplier') {
                            fillSelectField(`[name="${selector}"]`, value);
                        } else if (selector === 'street') {
                            fillSelectStreet(`[name="${selector}"]`, value);
                        } else {
                            fillTextField(`[name="${selector}"]`, value);
                        }
                    }
                });

                if(postalCode && city) {
                    const postalCodeCityText  = [postalCode, city].filter(Boolean).join(' ');
                    const postalCodeCityValue  = JSON.stringify({ code: postalCode, city: city });

                    fillSelectZip('[name="postalcode_ort"]', postalCodeCityValue, postalCodeCityText);
                }

                resolve();
            } catch (error) {
                reject('Failed to apply correction data.');
            }
        });
    }

    getNestedValue(obj, path) {
        if (!obj || !path) return null;
        return path.split('.').reduce((acc, key) => {
            return acc && acc[key] !== undefined ? acc[key] : null;
        }, obj);
    }


    renderForm() {
        let sendData = {};

        if (!this.tokenValue) {
            $(SELECTORS.FORM_CONTAINER).html(`<div class="page-title error-token"><h1>Herzlich Willkommen!</h1> <div class="text-2"><p>Diesen Seite kann nur mit einem gültigen Token geöffnet werden. Sollten wir Deine Mithilfe bei der Anmeldung Deines Stromvertrages benötigen, schicken wir Dir eine E-Mail mit einem persönlichen Token zu. Falls Du keine solche Mail erhalten hast musst Du nichts unternehmen.</p></div></div>`);
            return;
        }

        this.getCorrectionData()
            .then(data => {
                if (!data.correctionData) {
                    return Promise.reject('Correction data is missing.');
                }

                this.correctionData = data.correctionData;
                console.log('Processing correction data:', data.correctionData);
                return this.getEmptyForm().then(() => data.correctionData);
            })
            .then(correctionData => {
                this.hideLoader();
                this.loading = false;

                this.initializeForm();
                console.log('Form initialized.');

                return this.setCorrectionData(correctionData);
            })
            .then(() => {
                console.log('Correction data applied to the form successfully.');

                this.validateForm();
            })
            .catch(error => {
                this.hideLoader();
                this.loading = false;

                console.error('Failed to render form:', error);
                $(SELECTORS.FORM_CONTAINER).html(
                    `<div class="page-title error-token">
                                <h1>Vielen Dank für Deine Mithilfe. Wir konnten Deine aktuellen Daten nicht laden. Dies kann zwei Ursachen haben:</h1>
                                <div class="text-2">
                                    <ol>
                                        <li>Dein persönlicher Link läuft aus Datenschutzgründen nach 7 Tagen ab. Bitte schau in Deinen Mails, ob Du eine neuere Mail von uns erhalten hast.</li>
                                        <li>Du hast Deine korrigierten Daten bereits erfolgreich übermittelt. In diesem Fall haben wir Deinen Wechselprozess neu angestoßen und Du wirst in Kürze von uns hören. Weitere Anpassungen Deiner Daten kannst Du jederzeit im Kundenportal vornehmen.</li>
                                    </ol>
                                </div>
                            </div>`
                );
            });
    }

    updateCorrectionData() {
        const errorsContainer = $(SELECTORS.RESPONSE_ERRORS);

        return new Promise((resolve, reject) => {

            if(this.correctionData.contract.state && (this.correctionData.contract.state === 'CANCELED' || this.correctionData.contract.state === 'LOCKED')) {
                errorsContainer.append('<div class="error">Vielen Dank für Deine Mithilfe! Wir konnten Deine Anpassungen nicht speichern. Dein Vertrag ist nicht mehr aktiv. Für mehr Informationen prüfe bitte Deine E-Mails oder wende Dich an unseren Kundenservice.</div>');
                return reject('CANCELED || LOCKED');
            } else if(!this.correctionData.contract.state || (this.correctionData.contract.state && this.correctionData.contract.state !== 'CANCELED' && this.correctionData.contract.state !== 'LOCKED' && this.correctionData.contract.state !== 'GPKE_ABORTED')) {
                errorsContainer.append('<div class="error">Vielen Dank für Deine Mithilfe! Wir konnten Deine Anpassungen nicht speichern. Dein Vertragsstatus hat sich geändert und wir konnten den Anmeldeprozess mit Deinem Netzanbieter neu starten. Wir melden uns bei Dir, sobald wir Neuigkeiten haben.</div>');
                return reject('!CANCELED && !LOCKED && !GPKE_ABORTED');
            }

            try {
                // Utility function for date formatting
                const formatDateToISO = (dateString) => {
                    const date = new Date(dateString);
                    return date.toISOString();
                };

                // Get code & city
                const parsed = JSON.parse($(SELECTORS.POSTAL_CODE).val());
                if (parsed && parsed.code && parsed.city) {
                    this.setNestedValue(this.correctionData, 'supplyAddress.postalCode', parsed.code);
                    this.setNestedValue(this.correctionData, 'supplyAddress.city', parsed.city);
                }

                if (this.correctionData.businessPartner && this.correctionData.businessPartner.dateOfBirth) {
                    this.correctionData.businessPartner.dateOfBirth = this.convertTimestampToISO(
                        this.correctionData.businessPartner.dateOfBirth
                    );
                }

                // Iterate over `fieldMapping` to update `sendData` based on form inputs
                Object.entries(fieldMapping).forEach(([key, name]) => {
                    const field = $(`[name="${name}"]`);
                    if (field.length) {
                        let value = field.val();
                        if (field.is('select')) {
                            value = field.find('option:selected').val(); // For select fields
                        } else if (field.hasClass('flatpickr-input')) {
                            value = formatDateToISO(field.val()); // For date fields
                        }

                        const finalValue = value !== undefined && value !== null && value.trim() !== '' ? value : null;
                        this.setNestedValue(this.correctionData, key, finalValue); // Update the nested value in `this.correctionData`
                    }
                });

                // If success -> resolve()
                resolve('updateCorrectionData completed successfully');
            } catch (error) {
                // If error -> reject(error)
                reject(error);
            }
        });

    }

    createSendDataForCancellation() {
        let sendData = {};

        // Filter correction data to include only allowed fields
        Object.entries(allowedFields).forEach(([key, value]) => {
            if (typeof value === 'boolean' && value === true) {
                // Directly include the key if it’s a boolean and true
                const nestedValue = this.getNestedValue(this.correctionData, key);
                if (nestedValue !== undefined) {
                    this.setNestedValue(sendData, key, nestedValue);
                }
            } else if (Array.isArray(value)) {
                // For nested objects, include only allowed subfields
                const nestedObject = this.getNestedValue(this.correctionData, key);
                if (nestedObject) {
                    sendData[key] = {};
                    value.forEach((subKey) => {
                        if (nestedObject[subKey] !== undefined) {
                            sendData[key][subKey] = nestedObject[subKey];
                        }
                    });
                }
            }
        });

        if (!this.tokenValue) {
            console.error('Token value is missing.');
            this.loading = false;
            return;
        }

        // Add token value to the request payload
        sendData.tokenValue = this.tokenValue;

        return sendData;
    }

    createSendDataForTask() {
        console.log(this.correctionData);


        const currentDate = new Date().toISOString();
        const postalcodeAndOrt = this.getNestedValue(this.correctionData, 'supplyAddress.postalCode')+' '+this.getNestedValue(this.correctionData, 'supplyAddress.city');


        const fieldsData = {
            'Vorname': this.getNestedValue(this.correctionData, 'businessPartner.firstName'),
            'Nachname': this.getNestedValue(this.correctionData, 'businessPartner.name'),
            'Zählernummer': this.getNestedValue(this.correctionData, 'meterNumber'),
            'Vertragsnummer LFA': this.getNestedValue(this.correctionData, 'supplierOldCustomerId'),
            'LFA ILN': this.getNestedValue(this.correctionData, 'supplierOldILN'),
            'Straße': this.getNestedValue(this.correctionData, 'supplyAddress.street'),
            'Hausnummer': this.getNestedValue(this.correctionData, 'supplyAddress.housenumber'),
            'Hausnummer Zusatz': this.getNestedValue(this.correctionData, 'supplyAddress.housenumberAddition'),
            'Postleitzahl und Ort': postalcodeAndOrt, // Assuming postalcodeAndOrt is already defined
        };

        let description =
            'Der Kunde hat uns über die CLP korrigierte Daten übermittelt. Bitte vergleiche die unten aufgeführten Informationen mit den Daten im System und passe die falschen Systemdaten an. ' +
            'Bitte prüfe, ob im Anschluss der Lieferbeginn neu gestartet werden kann. Falls dies möglich ist, starte bitte einen neuen Lieferbeginn.';

        Object.entries(fieldsData).forEach(([label, value]) => {
            if (value) {
                description += ` ${label}: ${value};`;
            }
        });

        const sendData = {
            businessPartnerNumber: this.getNestedValue(this.correctionData, 'businessPartner.businessPartnerNumber') || '',
            contractNumber: this.getNestedValue(this.correctionData, 'contract.contractNr') || '',
            description: description,
            dueDate: currentDate,
            priority: 1,
            severity: 0,
            taskKey: 'CLP',
            title: 'CLP - Manuelle Datenkorrektur'
        };

        return sendData;
    }

    requestsToApiCancellation(sendData) {
        const Api = this;
        const errorsContainer = $(SELECTORS.RESPONSE_ERRORS);

        return new Promise((resolve, reject) => {
            console.log('Prepared data for submission:', sendData);

            // Send the updated data via AJAX
            $.ajax({
                url: `${this.proxyUrl}?type=saveCorrectionDataByCancellation`,
                method: 'POST',
                contentType: 'application/json',
                data: JSON.stringify(sendData), // Serialize the payload as JSON
                headers: {
                    'Authorization': this.authorizationHeader, // Add authorization header
                },
                success: function (response) {
                    // Check if there are validation results
                    if (response.validationResults && response.validationResults.length > 0) {
                        // Handle validation errors
                        errorsContainer.empty(); // Clear previous errors, if any

                        // Iterate through validationResults and display each error
                        response.validationResults.forEach(function (errorItem) {
                            const errorMessage = errorItem.errorMessage || 'Unknown error';
                            errorsContainer.append('<div class="error">'+errorMessage+'</div>');
                        });

                        Api.loading = false;

                        reject('Validation errors occurred');
                    } else {
                        console.log('Data saved successfully requestsToApiCancellation:', response); // Log success message

                        resolve(response);
                    }
                },
                error: function (xhr, status, error) {
                    console.error('Failed to save data:', error); // Log error details
                    errorsContainer.append('<div class="error">Failed to save data: '+error+'</div>');

                    Api.loading = false;
                    reject(error);
                }
            });
        });
    }

    requestsToApiTask(sendData) {
        const Api = this;
        const errorsContainer = $(SELECTORS.RESPONSE_ERRORS);

        return new Promise((resolve, reject) => {
            console.log('Prepared data for submission (Task):', sendData);

            // Send the updated data via AJAX
            $.ajax({
                url: `${this.proxyUrl}?type=createTask`,
                method: 'POST',
                contentType: 'application/json',
                data: JSON.stringify(sendData), // Serialize the payload as JSON
                headers: {
                    'Authorization': this.authorizationHeader, // Add authorization header
                },
                success: function (response) {
                    console.log('Data saved successfully requestsToApiTask:', response); // Log success message

                    resolve(response);
                },
                error: function (xhr, status, error) {
                    console.error('Failed to save data:', error); // Log error details
                    errorsContainer.append('<div class="error">Failed to save data: '+error+'</div>');

                    Api.loading = false;
                    reject(error);
                }
            });
        });
    }


    handleFormSubmit(event) {
        event.preventDefault();

        if (this.loading === true) {
            return; // Prevent duplicate submissions
        }
        this.loading = true;

        let sendDataForDisable = {
            token: ''
        };

        this.updateCorrectionData()
            .then(() => {
                console.log('updateCorrectionData completed.');

                if (this.nameCanged) {
                    return this.createSendDataForTask();
                } else {
                    return this.createSendDataForCancellation();
                }
            })
            .then((sendData) => {

                if (!sendData) {
                    throw new Error('No sendData available');
                }

                if (this.nameCanged) {
                    return this.requestsToApiTask(sendData);
                } else {
                    return this.requestsToApiCancellation(sendData);
                }
            })
            .then(() => {

                console.log('disableToken');

                if (this.nameCanged) {
                    sendDataForDisable.token = this.tokenValue;

                    return new Promise((resolve, reject) => {
                        $.ajax({
                            url: `${this.proxyUrl}?type=disableToken`,
                            method: 'POST',
                            contentType: 'application/json',
                            data: JSON.stringify(sendDataForDisable),
                            headers: {
                                'Authorization': this.authorizationHeader,
                            },
                            success: (response) => {
                                resolve(response);
                            },
                            error: (xhr, status, error) => {
                                console.error('Failed to disable token:', error);
                                reject(error);
                            }
                        });
                    });
                } else {
                    return Promise.resolve();
                }
            })
            .then(() => {
                console.log('All requests finished successfully. Redirect!');

                window.location.href = `/clp-success?email=${this.correctionData.businessPartner.email}&firstname=${this.correctionData.businessPartner.firstName}`;
            })
            .catch(error => {
                console.error('Error in chain:', error);
                this.loading = false;
            });
    }

}

// Initialize Revision API service
const revisionApiService = new RevisionApiService();

